import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import "../App.css";
import Dropdown from "./Dropdown.js";
import useFetchDropdownOptions from "../hooks/UseFetchDropdownOptions.js";
import { api, API_ORIGIN } from "../apiClient.js";
import { useAuthContext } from '../contexts/AuthContext.js';

/**
 * CreateMeeting
 * - 選擇案件(case)
 * - 挑選一位或多位出席人員
 * - 用月曆點擊日期 + 選時間建立會議
 * - 月曆上顯示已建立的會議
 */
export default function CreateMeeting() {
  const { user } = useAuthContext();

  // ====== API base ======
  // const API_ORIGIN = (window.__ENV && window.__ENV.API_ORIGIN) || "http://localhost:5000";

  // ====== Case dropdown ======
  const {
    CaseNameOptions,
    setCaseNameOptions,
    SelectedCaseName,
    setSelectedCaseName,
    handleOptionChange,
  } = useFetchDropdownOptions({
    apiUrl: `${API_ORIGIN}/options/case_name`,
    // apiUrl: 'http://localhost:5000/options/case_name', // API 路徑
    optionKey: "value",
    labelKey: "label",
    dataKey: null,
    defaultOptionsName: "CaseNameOptions",
    defaultSelectedName: "SelectedCaseName",
    transformItem: (item) => ({ value: item.value, label: item.label }),
  });

  // ====== Attendees (multi-select with checkboxes) ======
  const [attendeeOptions, setAttendeeOptions] = useState([
    { value: "JJ", label: "JJ" },
    { value: "Minda", label: "Minda" },
    { value: "Sean", label: "Sean" },
    { value: "Jasmine", label: "Jasmine" },
    { value: "Jia", label: "Jia" },
    { value: "Gao", label: "Gao" },
    { value: "Fish", label: "Fish" },
  ]); // [{value, label}]
  const [selectedAttendees, setSelectedAttendees] = useState([]); // values only
  const [attendeeQuery, setAttendeeQuery] = useState("");

  // useEffect(() => {
  //   const fetchAttendees = async () => {
  //     try {
  //       const { data } = await axios.get(`${API_ORIGIN}/options/assignee`);
  //       // 期望格式: [{ value: "user_id", label: "顯示名稱" }, ...]
  //       if (Array.isArray(data)) {
  //         // 將新資料加入現有陣列，並過濾重複 value
  //         setAttendeeOptions((prev) => {
  //           const combined = [...prev, ...data];
  //           const unique = combined.filter(
  //             (v, i, arr) => arr.findIndex((x) => x.value === v.value) === i
  //           );
  //           return unique;
  //         });
  //       }
  //     } catch (e) {
  //       console.warn("無法取得出席人員選單，請確認後端 /options/assignee:", e);
  //       setAttendeeOptions([]);
  //     }
  //   };
  //   fetchAttendees();
  // }, [API_ORIGIN]);

  useEffect(() => {
    const fetchAttendees = async () => {
      try {
        // const { data } = await axios.get("http://localhost:5000/users");
        const { data } = await api.get("/users");
        // Flask 回傳: { current_user: {...}, users: [...] }
        const userList = Array.isArray(data?.users) ? data.users : [];

        // console.log("userList:",userList)

        // 轉成前端選單格式
        const mapped = userList.map((u) => ({
          value: u.user_id,
          label: u.user_name || u.user_email || `User ${u.id}`,
          email: u.user_email,
        }));

        // 將新資料加入現有陣列，並過濾重複 value
        setAttendeeOptions((prev) => {
          const combined = [...prev, ...mapped];
          const unique = combined.filter(
            (v, i, arr) => arr.findIndex((x) => x.value === v.value) === i
          );
          return unique;
        });
      } catch (e) {
        console.warn("無法取得出席人員選單，請確認後端 /users:", e);
        setAttendeeOptions([]);
      }
    };
    fetchAttendees();
  }, []);


  const filteredAttendeeOptions = useMemo(() => {
    if (!attendeeQuery.trim()) return attendeeOptions;
    const q = attendeeQuery.toLowerCase();
    return attendeeOptions.filter((opt) =>
      `${opt.label} ${opt.value}`.toLowerCase().includes(q)
    );
  }, [attendeeOptions, attendeeQuery]);

  // useEffect(()=>{
  //   console.log("selectedAttendees:",selectedAttendees)
  // },[selectedAttendees])

  // useEffect(()=>{
  //   console.log("filteredAttendeeOptions:",filteredAttendeeOptions)
  // },[filteredAttendeeOptions])

  const toggleAttendee = (value) => {
    setSelectedAttendees((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // ====== Calendar state ======
  const tz = "Asia/Taipei"; // 顯示/解釋用
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth()); // 0-11
  const [selectedDate, setSelectedDate] = useState(new Date()); // 用於建立

  // time inputs
  const [startTime, setStartTime] = useState("10:00"); // HH:mm
  const [durationMin, setDurationMin] = useState(60);

  // meetings

  const fakeMeetings = [
    {
      id: 1,
      title: "設計評審會議",
      case_name: "新官網重構案",
      start: "2025-10-04T10:00:00+08:00",
      end: "2025-10-04T11:30:00+08:00",
      attendees: [
        { id: 1, name: "王小明" },
        { id: 2, name: "李佳雯" },
        { id: 3, name: "陳冠宇" },
      ],
    },
    {
      id: 2,
      title: "開發進度檢討",
      case_name: "AI 客服系統",
      start: "2025-11-04T14:00:00+08:00",
      end: "2025-11-04T15:00:00+08:00",
      attendees: [
        { id: 4, name: "林怡君" },
        { id: 2, name: "李佳雯" },
      ],
    },
    {
      id: 3,
      title: "客戶簡報準備",
      case_name: "力平方行銷專案",
      start: "2025-11-07T09:30:00+08:00",
      end: "2025-11-07T10:30:00+08:00",
      attendees: [
        { id: 5, name: "張志豪" },
        { id: 6, name: "許庭瑋" },
      ],
    },
    {
      id: 4,
      title: "雙週 Sprint 回顧",
      case_name: "內部開發流程改善",
      start: "2025-11-11T16:00:00+08:00",
      end: "2025-11-11T17:00:00+08:00",
      attendees: [
        { id: 1, name: "王小明" },
        { id: 3, name: "陳冠宇" },
        { id: 7, name: "林思妤" },
      ],
    },
    {
      id: 5,
      title: "客戶需求訪談",
      case_name: "永欣電商平台",
      start: "2025-11-14T13:00:00+08:00",
      end: "2025-11-14T14:30:00+08:00",
      attendees: [
        { id: 8, name: "何文婷" },
        { id: 9, name: "蔡博仁" },
      ],
    },
  ];

  const [meetings, setMeetings] = useState(fakeMeetings);
  // const [meetings, setMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);

  const fetchMeetings = async () => {
    setLoadingMeetings(true);
    try {
      // 可支援查詢區間: /meetings?start=ISO&end=ISO
      const monthStart = new Date(viewYear, viewMonth, 1);
      const monthEnd = new Date(viewYear, viewMonth + 1, 1);
      // const { data } = await axios.get(`${API_ORIGIN}/meetings`, {
      const { data } = await api.get("/meetings", {
        // "2025-11-14T07:00:00.000Z"
        params: { start: monthStart.toISOString(), end: monthEnd.toISOString() },
      });
      // 期望格式: [{ id, case_name, attendees:[value], start, end }]
      setMeetings(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn("讀取會議失敗，請確認後端 /meetings:", e);
      setMeetings([]);
    } finally {
      setLoadingMeetings(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_ORIGIN, viewYear, viewMonth]);

  // ====== Helpers ======
  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayOfMonth = (y, m) => new Date(y, m, 1).getDay(); // 0 Sun - 6 Sat

  const monthDays = useMemo(() => {
    const count = daysInMonth(viewYear, viewMonth);
    return [...Array(count)].map((_, i) => new Date(viewYear, viewMonth, i + 1));
  }, [viewYear, viewMonth]);

  const weeks = useMemo(() => {
    // 生成 6 週格子以固定高度
    const firstDow = firstDayOfMonth(viewYear, viewMonth);
    const total = 42; // 7 * 6
    const arr = [];
    for (let i = 0; i < total; i++) {
      const dayOffset = i - firstDow + 1;
      const d = new Date(viewYear, viewMonth, dayOffset);
      arr.push(d);
    }
    // console.log("arr:",arr);
    return arr;
  }, [viewYear, viewMonth]);

  // 將會議依日期歸類
  const meetingsByDateKey = useMemo(() => {
    const map = new Map();
    meetings.forEach((mt) => {
      const d = new Date(mt.start);
      const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(mt);
    });
    return map;
  }, [meetings]);

  const keyOfDate = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  const isSameMonth = (d, y, m) => d.getFullYear() === y && d.getMonth() === m;
  const isToday = (d) => {
    const t = new Date();
    return (
      d.getFullYear() === t.getFullYear() &&
      d.getMonth() === t.getMonth() &&
      d.getDate() === t.getDate()
    );
  };

  // ====== Submit ======
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!SelectedCaseName) {
      setMessage("請先選擇案件");
      return;
    }
    if (selectedAttendees.length === 0) {
      setMessage("請至少選擇 1 位出席人員");
      return;
    }

    const [hh, mm] = startTime.split(":").map((s) => parseInt(s, 10));
    const start = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      hh,
      mm,
      0,
      0
    );
    const end = new Date(start.getTime() + durationMin * 60 * 1000);

    const payload = {
      user_id: user.user_id,
      case_name: SelectedCaseName,
      // attendees: [1, 2],
      attendee_ids: selectedAttendees,
      start: start.toISOString(),
      end: end.toISOString(),
    };

    setSubmitting(true);
    try {
      console.log("submit payload:", payload)
      // const { data } = await axios.post(`${API_ORIGIN}/meetings`, payload);
      const { data } = await api.post("/meetings", payload);
      setMessage("已建立會議 ✅");
      // 重刷會議
      await fetchMeetings();
    } catch (e) {
      console.error(e);
      setMessage("建立會議失敗，請稍後再試或檢查後端");
    } finally {
      setSubmitting(false);
    }
  };


  // function TimeWheel({ label, value, onChange }) {
  //   const [h = "00", m = "00"] = (value || "00:00").split(":");
  //   const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  //   const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
  //   const boxStyle = { border: "1px solid #ddd", borderRadius: 8, maxHeight: 140, overflowY: "auto", scrollSnapType: "y mandatory", background: "#fff" };
  //   const itemStyle = (active) => ({ padding: "6px 8px", textAlign: "center", cursor: "pointer", scrollSnapAlign: "start", background: active ? "#fff7ed" : "#fff", fontWeight: active ? 700 : 500 });
  //   return (
  //     <div>
  //       <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{label}</div>
  //       <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
  //         <div style={boxStyle}>
  //           {hours.map((hr) => (
  //             <div key={hr} onClick={() => onChange(`${hr}:${m}`)} style={itemStyle(hr === h)}>{hr}</div>
  //           ))}
  //         </div>
  //         <div style={boxStyle}>
  //           {minutes.map((mi) => (
  //             <div key={mi} onClick={() => onChange(`${h}:${mi}`)} style={itemStyle(mi === m)}>{mi}</div>
  //           ))}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }


  // ====== UI ======


  function TimeWheel({ label, value = "10:00", onChange }) {
    const [h = "00", m = "00"] = (value || "00:00").split(":");
    const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
    const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
    const boxStyle = {
      border: "1px solid #ddd",
      borderRadius: 8,
      maxHeight: 140,
      overflowY: "auto",
      scrollSnapType: "y mandatory",
      background: "#fff",
    };
    const itemStyle = (active) => ({
      padding: "6px 8px",
      textAlign: "center",
      cursor: "pointer",
      scrollSnapAlign: "start",
      background: active ? "#fff7ed" : "#fff",
      fontWeight: active ? 700 : 500,
    });

    // ✅ 讓滾輪自動捲到預設時間
    const hourRef = React.useRef(null);
    const minuteRef = React.useRef(null);
    React.useEffect(() => {
      if (hourRef.current) {
        const activeEl = hourRef.current.querySelector(`[data-hour='${h}']`);
        if (activeEl) activeEl.scrollIntoView({ block: "center" });
      }
      if (minuteRef.current) {
        const activeEl = minuteRef.current.querySelector(`[data-minute='${m}']`);
        if (activeEl) activeEl.scrollIntoView({ block: "center" });
      }
    }, [h, m]);

    return (
      <div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{label}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div style={boxStyle} ref={hourRef}>
            {hours.map((hr) => (
              <div
                key={hr}
                data-hour={hr}
                onClick={() => onChange(`${hr}:${m}`)}
                style={itemStyle(hr === h)}
              >
                {hr}
              </div>
            ))}
          </div>
          <div style={boxStyle} ref={minuteRef}>
            {minutes.map((mi) => (
              <div
                key={mi}
                data-minute={mi}
                onClick={() => onChange(`${h}:${mi}`)}
                style={itemStyle(mi === m)}
              >
                {mi}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="sidebar-form">
      <div className="sidebar-header">— Meeting —</div>

      <form onSubmit={handleSubmit} className="sidebar-block" style={{ gap: 12 }}>
        <div className="blue-block">
          <img src="./doc.png" alt="doc" style={{ width: "7%" }} />
          Create Meeting
        </div>

        {/* Case */}
        <div className="block-row">
          <label>Case:</label>
          <Dropdown
            label="Case Name: "
            name="case_name"
            options={CaseNameOptions}
            value={SelectedCaseName}
            // onChange={(v) => {
            //   handleOptionChange(v);
            //   // setSelectedCaseName(v);
            // }}
            onChange={
              handleOptionChange
            }
            setOptions={setCaseNameOptions}
            disableAutoFetch={true}
            block="database-doropdown"
          />
        </div>

        {/* Attendees */}
        <div className="block-row" style={{ alignItems: "flex-start" }}>
          <label style={{ paddingTop: 6 }}>Attendees:</label>
          <div style={{
            // flex: 1,
            width: "60%",
          }}>
            <input
              type="text"
              placeholder="搜尋人員…"
              value={attendeeQuery}
              onChange={(e) => setAttendeeQuery(e.target.value)}
              className="input"
              style={{ marginBottom: 8, width: "100%" }}
            />
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 8,
                // padding: 8,
                maxHeight: 100,
                overflowY: "auto",
                background: "#fff",
              }}
            >
              {filteredAttendeeOptions.length === 0 && (
                <div style={{ color: "#999" }}>— 無人員資料 —</div>
              )}
              {filteredAttendeeOptions.map((opt) => (
                <label key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}>
                  <input
                    type="checkbox"
                    checked={selectedAttendees.includes(opt.value)}
                    onChange={() => toggleAttendee(opt.value)}
                    style={{ width: "30%", }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            {selectedAttendees.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#555" }}>
                已選：{selectedAttendees.length} 人
              </div>
            )}
          </div>
        </div>

        {/* Calendar + time */}
        <div className="block-row" style={{ alignItems: "flex-start", width: "100%", }}>
          {/* <label style={{ paddingTop: 6 }}>Date & Time:</label> */}
          <div style={{ flex: 1 }}>
            {/* Month header */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: 8, gap: 5 }}>
              <button type="button" className="month-button" onClick={() => setViewMonth((m) => (m === 0 ? (setViewYear((y) => y - 1), 11) : m - 1))}>
                ◀
              </button>
              <div style={{ fontWeight: 600 }}>{viewYear} / {viewMonth + 1}</div>
              <button type="button" className="month-button" onClick={() => setViewMonth((m) => (m === 11 ? (setViewYear((y) => y + 1), 0) : m + 1))}>
                ▶
              </button>
            </div>

            {/* Weekday header */}
            <div className="calendar-grid calendar-week-head">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
                <div key={w} className="calendar-cell calendar-head">{w}</div>
              ))}
            </div>

            {/* Month grid */}
            <div className="calendar-grid">
              {weeks.map((d, idx) => {

                const k = keyOfDate(d);
                const list = meetingsByDateKey.get(k) || [];
                const inMonth = isSameMonth(d, viewYear, viewMonth);
                const isSel = keyOfDate(d) === keyOfDate(selectedDate);

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSelectedDate(d)}
                    className={`calendar-cell ${inMonth ? "" : "calendar-out"} ${isSel ? "calendar-selected" : ""} ${isToday(d) ? "calendar-today" : ""}`}
                    title={list.length ? `${list.length} 筆會議` : undefined}
                  >
                    <div className="calendar-daynum">{d.getDate()}</div>
                    {/* 會議列：緊接日期下方，框線卡片 */}
                    <div className="calendar-events">
                      {list.length === 0 && <div className="calendar-empty">—</div>}
                      {list.map((mt) => (
                        <div
                          key={mt.id || mt.start}
                          className="calendar-event"
                          title={`${mt.case_name} ${new Date(mt.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`}
                        >
                          <div className="calendar-event-time">
                            {new Date(mt.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                          </div>
                          <div className="calendar-event-name">{mt.case_name}</div>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* time & duration */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
              {/* <div> */}
              {/* <div style={{ fontSize: 12, color: "#666" }}>開始時間（{tz}）</div> */}
              {/* <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="input"
                  step="60" lang="zh-TW"
                  required
                /> */}
              <TimeWheel label="開始時間（24h）" value={startTime} onChange={setStartTime} />
              {/* </div> */}
              {/* <div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>會議時長（分鐘）</div>
                <input
                  type="number"
                  min={15}
                  step={15}
                  value={durationMin}
                  onChange={(e) => setDurationMin(parseInt(e.target.value || "0", 10))}
                  className="input"
                  required
                />
              </div> */}
            </div>

          </div>
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: "#555" }}>
          已選日期：{selectedDate.toLocaleDateString()}
          ；時間：{startTime}
          {/* ，時長 {durationMin} 分鐘 */}
        </div>
        <button className="orange-button" type="submit" disabled={submitting}>
          {submitting ? "建立中…" : "建立會議"}
        </button>

        {message && (
          <div style={{ marginTop: 8, color: message.includes("✅") ? "#0a8" : "#c33" }}>{message}</div>
        )}
      </form>

      {/* Existing meetings list */}
      <div className="database-selected" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="./complete.png" alt="done" style={{ width: "8%" }} />
          本月會議（{loadingMeetings ? "讀取中…" : `${meetings.length} 筆`}）
        </div>
        <ul style={{ marginTop: 8 }}>
          {meetings.map((m) => (
            <li key={m.id || m.start} style={{ marginBottom: 6 }}>
              <div>
                {new Date(m.start).toLocaleString()}
                {/* - {new Date(m.end).toLocaleTimeString()} */}
              </div>
              <strong>{m.case_name}</strong>
              {Array.isArray(m.attendees) && m.attendees.length > 0 && (
                <span style={{ marginLeft: 8, color: "#666" }}>👥 {m.attendees.length} 人</span>
              )}
            </li>
          ))}
          {meetings.length === 0 && !loadingMeetings && <li style={{ color: "#999" }}>— 本月尚無會議 —</li>}
        </ul>
      </div>

      {/* Minimal styles for calendar (optional) */}
      <style>{`
      .month-button{
  border: solid #ED6737;
  background-color: #ED6737;
  border-radius: 20px;
  font-size: 10px;
  width: 20px;
  height: 20px;
  color: #ffffff;
      
      }
      
      .calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}
.calendar-week-head { margin-bottom: 4px; }

.calendar-cell {
  position: relative;
  min-height: 92px;
  border: 1px solid #e6e6e6;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  display: flex;              /* 垂直布局 */
  flex-direction: column;
  padding: 2px;
  text-align: left;
}

/* 取消瀏覽器預設 focus 外框，避免看起來“沒反應” */
.calendar-cell:focus { outline: none; }

.calendar-head { background: #f7f7f7; text-align: center; 
font-weight: 600;
  min-height: 35px;
 }
.calendar-out { opacity: 0.35; }
.calendar-today { box-shadow: inset 0 0 0 2px #1e90ff; }

/* ✅ 選中外框：不會被其他 outline/box-shadow 覆蓋 */
.calendar-cell.calendar-selected::after {
  content: "";
  position: absolute;
  inset: -3px;               /* 覆蓋整個格子，略微外擴 */
  border: 2px solid #ff922b; /* 你的選中顏色 */
  border-radius: 12px;       /* 稍比 cell 大一點，視覺更圓 */
  pointer-events: none;      /* 讓點擊不會卡在偽元素 */
}

/* 日期：置頂置中 */
.calendar-daynum {
  font-weight: 700;
  font-size: 12px;
  color: #333;
  text-align: center;
}

/* 會議列容器：緊接日期下方 */
.calendar-events {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-item: center;
  justify-content: center;

}
.calendar-empty {
  text-align: center;
  font-size: 11px;
  color: #bbb;
}

/* 會議小膠囊 */
.calendar-event {
  display: flex;
  flex-direction: column;
  align-item: center;
  justify-content: center;
  border: 1px solid #37E0A2;
  background-color: #37E0A2;
  border-radius: 8px;
  font-size: 8px;
  white-space: normal;
  word-break: break-word;
}
.calendar-event-time { font-weight: 700; }
.calendar-event-name { opacity: 0.9; }

      `}</style>
    </div>
  );
}
